import { useState } from 'react'

import { api } from '../api/client'
import type { Page, Schedule, Shift, Team, User } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, TableScroll, formatDateTime } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

interface OnCall {
  schedule_id: string
  at: string
  user_ids: string[]
}

export function SchedulesPage() {
  const { can } = useAuth()
  const schedules = useQuery(() => api.get<Page<Schedule>>('/schedules', { limit: 200 }), [])
  const teams = useQuery(() => api.get<Page<Team>>('/teams', { limit: 200 }), [])
  const users = useQuery(
    () =>
      can('users.manage') ? api.get<Page<User>>('/users', { limit: 500 }) : Promise.resolve(null),
    [],
  )
  const action = useAction()

  const [selected, setSelected] = useState<Schedule | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    team_id: '',
    name: '',
    timezone: 'Europe/Moscow',
    type: 'weekly' as 'daily' | 'weekly',
    participants: [] as string[],
  })

  const shifts = useQuery(
    () =>
      selected
        ? api.get<Page<Shift>>(`/schedules/${selected.id}/shifts`, { limit: 50 })
        : Promise.resolve(null),
    [selected?.id],
  )
  const oncall = useQuery(
    () => (selected ? api.get<OnCall>(`/schedules/${selected.id}/oncall`) : Promise.resolve(null)),
    [selected?.id],
  )

  const writable = can('schedules.write')

  const create = async () => {
    const ok = await action.run(() =>
      api.post('/schedules', {
        team_id: form.team_id,
        name: form.name,
        timezone: form.timezone,
        rotation: {
          type: form.type,
          participants: form.participants,
          length_hours: form.type === 'daily' ? 24 : 168,
        },
      }),
    )
    if (ok) {
      setCreating(false)
      schedules.reload()
    }
  }

  const userLabel = (id: string) => {
    const user = users.data?.items.find((item) => item.id === id)
    return user ? user.full_name || user.email : id.slice(0, 8)
  }

  return (
    <>
      <ErrorNote>{schedules.error ?? action.error}</ErrorNote>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card
          title="Расписания дежурств"
          actions={
            writable && (
              <button className="btn-primary" onClick={() => setCreating(true)}>
                Новое расписание
              </button>
            )
          }
        >
          {schedules.loading && !schedules.data ? (
            <Spinner />
          ) : (schedules.data?.items.length ?? 0) === 0 ? (
            <Empty>Расписаний нет — шаг эскалации «дежурный» никого не найдёт.</Empty>
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <th className="th">Название</th>
                  <th className="th">Команда</th>
                  <th className="th">Ротация</th>
                </tr>
              </thead>
              <tbody>
                {schedules.data!.items.map((schedule) => (
                  <tr
                    key={schedule.id}
                    onClick={() => setSelected(schedule)}
                    className={`cursor-pointer hover:bg-ink-700/40 ${
                      selected?.id === schedule.id ? 'bg-ink-700/60' : ''
                    }`}
                  >
                    <td className="td">{schedule.name}</td>
                    <td className="td text-slate-400">
                      {teams.data?.items.find((team) => team.id === schedule.team_id)?.name ?? '—'}
                    </td>
                    <td className="td text-slate-400 text-xs">
                      {schedule.rotation?.type ?? '—'} · участников:{' '}
                      {schedule.rotation?.participants?.length ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        </Card>

        <Card title={selected ? `Смены: ${selected.name}` : 'Смены'}>
          {!selected ? (
            <Empty>Выберите расписание слева.</Empty>
          ) : (
            <div>
              <div className="px-4 py-3 border-b border-ink-600 text-sm">
                <span className="text-slate-400">Сейчас дежурит: </span>
                <span className="text-slate-100">
                  {(oncall.data?.user_ids ?? []).map(userLabel).join(', ') || 'никто'}
                </span>
              </div>
              {(shifts.data?.items.length ?? 0) === 0 ? (
                <Empty>Смены ещё не развёрнуты.</Empty>
              ) : (
                <TableScroll>
                  <thead>
                    <tr>
                      <th className="th">Дежурный</th>
                      <th className="th">С</th>
                      <th className="th">По</th>
                      <th className="th">Тип</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.data!.items.map((shift) => (
                      <tr key={shift.id}>
                        <td className="td">{userLabel(shift.user_id)}</td>
                        <td className="td text-slate-400 tabular-nums">
                          {formatDateTime(shift.starts_at)}
                        </td>
                        <td className="td text-slate-400 tabular-nums">
                          {formatDateTime(shift.ends_at)}
                        </td>
                        <td className="td text-xs">
                          {shift.is_override ? (
                            <span className="text-amber-400">замещение</span>
                          ) : (
                            <span className="text-slate-500">ротация</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TableScroll>
              )}
            </div>
          )}
        </Card>
      </div>

      {creating && (
        <Card
          title="Новое расписание"
          actions={
            <>
              <button className="btn-ghost" onClick={() => setCreating(false)}>
                Отмена
              </button>
              <button className="btn-primary" disabled={action.pending} onClick={create}>
                Создать
              </button>
            </>
          }
        >
          <div className="p-4 grid lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Команда</label>
              <select
                className="input"
                value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
              >
                <option value="">—</option>
                {teams.data?.items.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Часовой пояс</label>
              <input
                className="input"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Тип ротации</label>
              <select
                className="input"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as 'daily' | 'weekly' })}
              >
                <option value="daily">посуточная</option>
                <option value="weekly">понедельная</option>
              </select>
            </div>
            <div className="lg:col-span-4">
              <label className="label">Участники ротации (порядок = очередь)</label>
              <select
                className="input"
                multiple
                size={5}
                value={form.participants}
                onChange={(e) =>
                  setForm({
                    ...form,
                    participants: Array.from(e.target.selectedOptions, (option) => option.value),
                  })
                }
              >
                {users.data?.items.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
